import React from 'react'
import {
  Admin,
  Resource,
  CustomRoutes,
  localStorageStore,
  StoreContextProvider,
} from 'react-admin'
import { Route } from 'react-router-dom'
import AdminInvitation from './pages/AdminInvitation'
import { Dashboard } from './Dashboard'
import { Login } from './Login'

import { UserShow, UserList, UserInvite } from './resources/users'
import { PhaseShow, PhaseList, PhaseCreate, PhaseEdit } from './resources/phases'
import { BotConfigShow, BotConfigList, BotConfigEdit } from './resources/botConfigs'
import {
  ServiceConfigShow,
  ServiceConfigList,
  ServiceConfigEdit,
} from './resources/serviceConfigs'
import { OfferShow, OfferList, OfferCreate, OfferEdit } from './resources/offers'
import { ArticleShow, ArticleList, ArticleCreate, ArticleEdit } from './resources/articles'
import { ModalShow, ModalList, ModalCreate, ModalEdit } from './resources/modals'
import {
  StaticRecommendationShow,
  StaticRecommendationList,
  StaticRecommendationCreate,
  StaticRecommendationEdit,
} from './resources/staticRecommendations'

import { useEffect } from 'react'
import { startOnMessage } from './libs/pushTemplates/utils/firebase'

// https://mui.com/material-ui/material-icons/?theme=Outlined&query=report
import UserIcon from '@mui/icons-material/Person'
import PhaseIcon from '@mui/icons-material/NightsStay'
import BotConfigIcon from '@mui/icons-material/SmartToy'
import ServiceConfigIcon from '@mui/icons-material/Tune'
import OfferIcon from '@mui/icons-material/LocalOffer'
import ArticleIcon from '@mui/icons-material/Feed'
import ModalIcon from '@mui/icons-material/MarkChatRead'
import StaticRecommendationIcon from '@mui/icons-material/TextSnippet'

import { authProvider, buildResourcePermissions } from './authProvider'
import { dataProvider } from './dataProvider'

import MyLayout from './layout/Layout'
import { ProfileEdit } from './pages/profile'
import { customDarkTheme, customLightTheme, logInBackgroundImage } from './theme/myCustomTheme'

// NotificationTemplate
import {
  NotificationTemplateCreate,
  NotificationTemplateEdit,
  NotificationTemplateList,
} from './libs/emailTemplates'
import DraftsIcon from '@mui/icons-material/Drafts'
import { projectInitialTemplate } from './config/projectInitialTemplate'

//PushTemplate
import { PushTemplateCreate, PushTemplateEdit, PushTemplateList } from './libs/pushTemplates'
import SpeakerNotesIcon from '@mui/icons-material/SpeakerNotes'

const resources = [
  <Resource
    name="botConfigs"
    list={BotConfigList}
    edit={BotConfigEdit}
    show={BotConfigShow}
    recordRepresentation="title"
    icon={BotConfigIcon}
    options={{ label: 'BotConfigs' }}
  />,
  <Resource
    name="serviceConfigs"
    list={ServiceConfigList}
    edit={ServiceConfigEdit}
    show={ServiceConfigShow}
    recordRepresentation="title"
    icon={ServiceConfigIcon}
    options={{ label: 'ServiceConfigs' }}
  />,
  <Resource
    name="users"
    list={UserList}
    show={UserShow}
    create={UserInvite}
    recordRepresentation="email"
    icon={UserIcon}
    options={{ label: 'Users' }}
  />,
  <Resource
    name="phases"
    list={PhaseList}
    edit={PhaseEdit}
    create={PhaseCreate}
    show={PhaseShow}
    recordRepresentation="title"
    icon={PhaseIcon}
    options={{ label: 'Phases' }}
  />,
  <Resource
    name="offers"
    list={OfferList}
    edit={OfferEdit}
    create={OfferCreate}
    show={OfferShow}
    recordRepresentation="userType"
    icon={OfferIcon}
    options={{ label: 'Offers' }}
  />,
  <Resource
    name="staticRecommendations"
    list={StaticRecommendationList}
    edit={StaticRecommendationEdit}
    create={StaticRecommendationCreate}
    show={StaticRecommendationShow}
    recordRepresentation="userType"
    icon={StaticRecommendationIcon}
    options={{ label: 'StaticRecommendations' }}
  />,
  <Resource
    name="articles"
    list={ArticleList}
    edit={ArticleEdit}
    create={ArticleCreate}
    show={ArticleShow}
    recordRepresentation="title"
    icon={ArticleIcon}
    options={{ label: 'Articles' }}
  />,
  <Resource
    name="modals"
    list={ModalList}
    edit={ModalEdit}
    create={ModalCreate}
    show={ModalShow}
    recordRepresentation="title"
    icon={ModalIcon}
    options={{ label: 'Modals' }}
  />,
  <Resource
    name="notificationTemplates"
    list={(props) => <NotificationTemplateList customEmail={false} {...props} />}
    edit={NotificationTemplateEdit}
    create={(props) => (
      <NotificationTemplateCreate
        projectInitialTemplate={projectInitialTemplate}
        resourceName="notificationTemplates"
        {...props}
      />
    )}
    icon={DraftsIcon}
    options={{ label: 'Email Templates' }}
  />,
  <Resource
    name="pushTemplates"
    list={(props) => <PushTemplateList customEmail={false} {...props} />}
    edit={PushTemplateEdit}
    create={(props) => (
      <PushTemplateCreate
        projectInitialTemplate={projectInitialTemplate}
        resourceName="pushTemplates"
        {...props}
      />
    )}
    icon={SpeakerNotesIcon}
    options={{ label: 'Push Templates' }}
  />,
]

const filterResources = () => {
  const resourcesMap = buildResourcePermissions()
  const resourceNames = Object.keys(resourcesMap)
  
  return resources
    .filter((r) => resourceNames.includes(r.props.name))
    .map((resource) => {
      const resourceName = resource.props.name
      const permissions = resourcesMap[resourceName]

      const newProps = {
        name: resource.props.name,
        recordRepresentation: resource.props.recordRepresentation,
        icon: resource.props.icon,
        options: resource.props.options
      }
      
      if (permissions.isRead) {
        if (resource.props.list) {
          newProps.list = typeof resource.props.list === 'function' 
            ? resource.props.list({ 
                isEdit: permissions.isEdit || false, 
                isDelete: permissions.isDelete || false,
                isCreate: permissions.isCreate || false,
              })
            : resource.props.list
        } else {
          newProps.list = undefined
        }
        if (resource.props.show) {
          newProps.show = resource.props.show
        } else {
          newProps.show = undefined
        }
      } else {
        newProps.list = undefined
        newProps.show = undefined
      }
      
      if (permissions.isEdit && resource.props.edit) {
        newProps.edit = resource.props.edit
      } else {
        newProps.edit = undefined
      }
      
      if (permissions.isCreate && resource.props.create) {
        newProps.create = resource.props.create
      } else {
        newProps.create = undefined
      }
      
      return React.cloneElement(resource, newProps)
    })
}

const store = localStorageStore(undefined, 'fem-fast-admin')

const App = () => {
  useEffect(() => {
    startOnMessage()
  }, [])

  return (
    <Admin
      loginPage={
        <Login
          style={{
            backgroundImage: logInBackgroundImage,
          }}
        />
      }
      dataProvider={dataProvider}
      authProvider={authProvider}
      store={store}
      dashboard={Dashboard}
      layout={MyLayout}
      theme={customLightTheme}
      darkTheme={customDarkTheme}
      defaultTheme="light"
    >
      <CustomRoutes>
        <Route key="my-profile" path="/my-profile" element={<ProfileEdit />} />
      </CustomRoutes>
      {filterResources}

      <CustomRoutes noLayout disableAuthentication>
        <Route key="admin-invitation" path="/admin-invitation" element={<AdminInvitation />} />
      </CustomRoutes>
    </Admin>
  )
}

const AppWrapper = () => (
  <StoreContextProvider value={store}>
    <App />
  </StoreContextProvider>
)

export default AppWrapper
